import React, { useCallback, useEffect, useState } from "react";
import { PageLayout } from "../../components/Page/PageLayout";
import { gql, useLazyQuery, useQuery } from "@apollo/client";
import { useKeycloak } from "@react-keycloak/web";
import { KeycloakAccessToken } from "../../lib/keycloakAccessToken";
import { ToastFormat, ToastType } from "../../shared/enums";
import { useToast } from "../../lib/useToast";
import { useVisiblePolling } from "../../lib/useVisiblePolling";
import { useRefreshError } from "../../lib/useRefreshError";
import {
  ActivitySortField,
  ActivitySearchParams,
  CustomerGroup as CustomerGroupEntity,
  SortOrder,
  VistActivityItem as VistActivityItemEntity,
} from "../../generated/graphql";
import { Option } from "react-select/src/filters";
import { limit } from "../../components/shared/pagination/helper";
import ActionsSection from "./ActionsSection";
import ActivitiesSection from "./ActivitiesSection";
import ActivitiesPagination from "./ActivitiesPagination";
import { handleDownloadActivities } from "./helper";

interface ActivitiesList {
  activities: VistActivityItemEntity[];
  count: number;
}

const queryActivitiesFields = `
    activityStart
    activityEnd
    createdBy {
      first_name
      last_name
      email
    }
    id
    unitName
    courseName
    totalTimeInSeconds
    result
    progress
    sourceUrl
    exerciseId
    exerciseName
    moduleId
    moduleName
    moduleVersion
    serviceTag
    ipAddress
    `;

const ActivityLogPage: React.FC = ({ children }) => {
  const { keycloak } = useKeycloak();
  const user: KeycloakAccessToken | undefined = keycloak?.tokenParsed;
  return (
    <ActivityLogSession key={user?.sub || ""} user={user}>
      {children}
    </ActivityLogSession>
  );
};

const ActivityLogSession: React.FC<{ user?: KeycloakAccessToken }> = ({
  user,
  children,
}) => {
  const [, createToast] = useToast();
  const sortOrderOptions: Option[] = [
    {
      value: "Date (Newest to oldest)",
      label: "Date (Newest to oldest)",
      data: {
        sortField: ActivitySortField.ActivityStart,
        sortOrder: SortOrder.Desc,
      },
    },
    {
      value: "Date (Oldest to newest)",
      label: "Date (Oldest to newest)",
      data: {
        sortField: ActivitySortField.ActivityStart,
        sortOrder: SortOrder.Asc,
      },
    },
    {
      value: "Course (A-Z)",
      label: "Course (A-Z)",
      data: {
        sortField: ActivitySortField.CourseName,
        sortOrder: SortOrder.Asc,
      },
    },
    {
      value: "Course (Z-A)",
      label: "Course (Z-A)",
      data: {
        sortField: ActivitySortField.CourseName,
        sortOrder: SortOrder.Desc,
      },
    },
    {
      value: "Exercise (A-Z)",
      label: "Exercise (A-Z)",
      data: {
        sortField: ActivitySortField.ExerciseName,
        sortOrder: SortOrder.Asc,
      },
    },
    {
      value: "Exercise (Z-A)",
      label: "Exercise (Z-A)",
      data: {
        sortField: ActivitySortField.ExerciseName,
        sortOrder: SortOrder.Desc,
      },
    },
    {
      value: "Score (Highest to lowest)",
      label: "Score (Highest to lowest)",
      data: {
        sortField: ActivitySortField.Score,
        sortOrder: SortOrder.Asc,
      },
    },
    {
      value: "Score (Lowest to highest)",
      label: "Score (Lowest to highest)",
      data: {
        sortField: ActivitySortField.Score,
        sortOrder: SortOrder.Desc,
      },
    },
  ];

  const [selectedSortOption, setSelectedSortOption] = useState<Option>(
    sortOrderOptions[0]
  );
  const [searchText, setSearchText] = useState("");
  const [checkedGroups, setCheckedGroups] = useState<number[]>([]);
  const [viewAllActivities, setViewAllActivities] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [allActivitiesCount, setAllActivitiesCount] = useState({
    searchKey: "",
    count: 0,
  });
  const [normalActivitiesCount, setNormalActivitiesCount] = useState({
    searchKey: "",
    count: 0,
  });
  const offset = (activePage - 1) * limit;
  const searchParams = {
    sortField: selectedSortOption.data.sortField,
    sortOrder: selectedSortOption.data.sortOrder,
    searchText,
  };
  const [groups, setGroups] = useState<{
    normal?: CustomerGroupEntity[];
    all?: CustomerGroupEntity[];
  }>({});
  const groupMode = viewAllActivities ? "all" : "normal";
  const onGroupsLoaded = useCallback(
    (loaded: CustomerGroupEntity[]) => {
      setGroups((previous) => ({ ...previous, [groupMode]: loaded }));
    },
    [groupMode]
  );
  const resetPagination = () => {
    setActivePage(1);
  };
  const reportExportError = (error: Error) => {
    console.error(error);
    createToast({
      title: "Activities could not be exported. Please try again.",
      type: ToastType.ERROR,
      format: ToastFormat.TOAST,
    });
  };
  const [fetchAllVistActivities, { loading: allExportLoading }] = useLazyQuery<{
    allVistActivities: ActivitiesList;
  }>(
    gql`
      query ExportVistActivities($searchParams: ActivitySearchParams) {
        allVistActivities(searchParams: $searchParams) {
          activities { ${queryActivitiesFields} }
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
      onCompleted: ({ allVistActivities: { activities } }) =>
        handleDownloadActivities(activities, createToast),
      onError: reportExportError,
    }
  );
  const [fetchMyVistActivities, { loading: myExportLoading }] = useLazyQuery<{
    vistActivities: VistActivityItemEntity[];
  }>(
    gql`
      query ExportMyVistActivities($groups: [Int!]!, $searchParams: ActivitySearchParams) {
        vistActivities(groups: $groups, searchParams: $searchParams) { ${queryActivitiesFields} }
      }
    `,
    {
      fetchPolicy: "no-cache",
      onCompleted: ({ vistActivities }) =>
        handleDownloadActivities(vistActivities, createToast),
      onError: reportExportError,
    }
  );
  const exporting = allExportLoading || myExportLoading;
  const onClickDownloadActivities = () => {
    if (exporting) return;
    if (viewAllActivities)
      fetchAllVistActivities({ variables: { searchParams } });
    else
      fetchMyVistActivities({
        variables: { groups: checkedGroups, searchParams },
      });
  };
  return (
    <PageLayout title="Mentice Live | Activity Log">
      {children}
      <ActionsSection
        checkedGroups={checkedGroups}
        selectedSortOption={selectedSortOption}
        shownCustomersGroup={groups[groupMode]}
        sortOrderOptions={sortOrderOptions}
        user={user}
        viewAllActivities={viewAllActivities}
        setCheckedGroups={(value) => {
          setCheckedGroups(value);
          resetPagination();
        }}
        setSearchText={(value) => {
          setSearchText(value);
          resetPagination();
        }}
        setSelectedSortOption={(value) => {
          setSelectedSortOption(value);
          resetPagination();
        }}
        handleViewAllActivitiesToggle={() => {
          setViewAllActivities((value) => !value);
          resetPagination();
        }}
        onClickDownloadActivities={onClickDownloadActivities}
        exporting={exporting}
      />
      <ActivityLogResults
        key={JSON.stringify([
          viewAllActivities,
          viewAllActivities ? [] : checkedGroups,
          searchParams,
          offset,
        ])}
        user={user}
        viewAllActivities={viewAllActivities}
        checkedGroups={checkedGroups}
        searchParams={searchParams}
        activePage={activePage}
        setActivePage={setActivePage}
        allActivitiesCount={allActivitiesCount}
        setAllActivitiesCount={setAllActivitiesCount}
        normalActivitiesCount={normalActivitiesCount}
        setNormalActivitiesCount={setNormalActivitiesCount}
        onGroupsLoaded={onGroupsLoaded}
      />
    </PageLayout>
  );
};

interface ActivityCount {
  searchKey: string;
  count: number;
}

interface ActivityLogResultsProps {
  user?: KeycloakAccessToken;
  viewAllActivities: boolean;
  checkedGroups: number[];
  searchParams: ActivitySearchParams;
  activePage: number;
  setActivePage: React.Dispatch<React.SetStateAction<number>>;
  allActivitiesCount: ActivityCount;
  setAllActivitiesCount: (value: ActivityCount) => void;
  normalActivitiesCount: ActivityCount;
  setNormalActivitiesCount: (value: ActivityCount) => void;
  onGroupsLoaded: (groups: CustomerGroupEntity[]) => void;
}

// Apollo 3.4 can deliver an in-flight poll after variables change. A keyed result
// component confines each query and its subscriptions to one page and filter set.
const ActivityLogResults = ({
  user,
  viewAllActivities,
  checkedGroups,
  searchParams,
  activePage,
  setActivePage,
  allActivitiesCount,
  setAllActivitiesCount,
  normalActivitiesCount,
  setNormalActivitiesCount,
  onGroupsLoaded,
}: ActivityLogResultsProps) => {
  const offset = (activePage - 1) * limit;
  const searchKey = JSON.stringify(searchParams);
  const normalSearchKey = JSON.stringify([checkedGroups, searchParams]);
  const activitiesQuery = useQuery<{
    customerGroupsByUser: CustomerGroupEntity[];
    vistActivityPage: ActivitiesList;
  }>(
    gql`
      query FetchVistActivity($groups: [Int!]!, $searchParams: ActivitySearchParams, $limit: Int!, $offset: Int!) {
        vistActivityPage(groups: $groups, searchParams: $searchParams, limit: $limit, offset: $offset) {
          activities { ${queryActivitiesFields} }
          count
        }
        customerGroupsByUser { id name }
      }
    `,
    {
      variables: { groups: checkedGroups, searchParams, limit, offset },
      skip: !user || viewAllActivities,
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    }
  );

  const allActivitiesQuery = useQuery<{
    allCustomerGroups: CustomerGroupEntity[];
    allVistActivities: ActivitiesList;
  }>(
    gql`
      query FetchAllVistActivity($searchParams: ActivitySearchParams, $limit: Int, $offset: Int) {
        allVistActivities(searchParams: $searchParams, limit: $limit, offset: $offset) {
          activities { ${queryActivitiesFields} }
          count
        }
        allCustomerGroups { id name }
      }
    `,
    {
      variables: { searchParams, limit, offset },
      skip: !user || !viewAllActivities,
      fetchPolicy: "no-cache",
      notifyOnNetworkStatusChange: true,
    }
  );
  useVisiblePolling(activitiesQuery, !!user && !viewAllActivities);
  useVisiblePolling(allActivitiesQuery, !!user && viewAllActivities);

  const query = viewAllActivities ? allActivitiesQuery : activitiesQuery;
  const queryKey = JSON.stringify([
    viewAllActivities,
    checkedGroups,
    searchParams,
    offset,
  ]);
  const error = useRefreshError(query, queryKey);
  const shownActivities = viewAllActivities
    ? allActivitiesQuery.data?.allVistActivities.activities
    : activitiesQuery.data?.vistActivityPage?.activities;
  const currentGroups = viewAllActivities
    ? allActivitiesQuery.data?.allCustomerGroups
    : activitiesQuery.data?.customerGroupsByUser;
  useEffect(() => {
    if (currentGroups) onGroupsLoaded(currentGroups);
  }, [currentGroups, onGroupsLoaded]);

  useEffect(() => {
    if (!viewAllActivities || query.loading || query.error || !shownActivities)
      return;
    if (offset === 0) {
      // The server only supplies a total on the first page.
      setAllActivitiesCount({
        searchKey,
        count: Math.max(
          allActivitiesQuery.data?.allVistActivities.count || 0,
          shownActivities.length
        ),
      });
    } else if (shownActivities.length === 0) {
      setActivePage(1);
    }
  }, [
    viewAllActivities,
    query.loading,
    query.error,
    shownActivities,
    offset,
    allActivitiesQuery.data,
    searchKey,
    setAllActivitiesCount,
    setActivePage,
  ]);

  const count = viewAllActivities
    ? Math.max(
        allActivitiesCount.searchKey === searchKey
          ? allActivitiesCount.count
          : 0,
        offset + (shownActivities?.length || 0)
      )
    : Math.max(
        normalActivitiesCount.searchKey === normalSearchKey
          ? normalActivitiesCount.count
          : 0,
        activitiesQuery.data?.vistActivityPage?.count || 0,
        shownActivities?.length ? offset + shownActivities.length : 0
      );
  const totalPages = Math.max(1, Math.ceil(count / limit));
  useEffect(() => {
    const page = activitiesQuery.data?.vistActivityPage;
    if (viewAllActivities || query.loading || query.error || !page) return;
    // Count and rows use separate statements; newly inserted rows may exceed the count.
    const confirmedCount = Math.max(
      page.count,
      page.activities.length ? offset + page.activities.length : 0
    );
    setNormalActivitiesCount({
      searchKey: normalSearchKey,
      count: confirmedCount,
    });
    setActivePage((current) =>
      Math.min(current, Math.max(1, Math.ceil(confirmedCount / limit)))
    );
  }, [
    viewAllActivities,
    query.loading,
    query.error,
    activitiesQuery.data,
    normalSearchKey,
    offset,
    setNormalActivitiesCount,
    setActivePage,
  ]);

  const retry = () => {
    void query.refetch().catch(() => undefined);
  };

  return (
    <>
      <ActivitiesSection
        loading={query.loading || !user}
        error={error}
        onRetry={retry}
        shownActivities={shownActivities}
      />
      {totalPages > 1 && (
        <ActivitiesPagination
          activitiesLoading={query.loading && shownActivities === undefined}
          allActivitiesLoading={false}
          activePage={activePage}
          totalPages={totalPages}
          setActivePage={setActivePage}
          setOffset={(value) => setActivePage(Math.floor(value / limit) + 1)}
        />
      )}
    </>
  );
};

export default ActivityLogPage;
